import { useState, FormEvent } from 'react';
import { Activity, AlertCircle, Loader2 } from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';

export function Login() {
  const { login, error, clearError } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [requiresTwoFactor, setRequiresTwoFactor] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    clearError();
    setIsLoading(true);

    try {
      const result = await login(
        email,
        password,
        requiresTwoFactor ? totpCode : undefined
      );

      if (result.requiresTwoFactor) {
        setRequiresTwoFactor(true);
      }
    } catch {
      // Error is handled in store
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-950 px-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <Activity className="h-8 w-8 text-pulse-400" />
          <span className="text-2xl font-semibold text-gray-100">Pulse</span>
        </div>

        {/* Login Form */}
        <div className="card">
          <div className="card-header">
            <h1 className="text-lg font-medium text-gray-100">
              {requiresTwoFactor ? 'Two-Factor Authentication' : 'Sign in'}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {requiresTwoFactor
                ? 'Enter your 2FA code to continue'
                : 'Access your infrastructure dashboard'}
            </p>
          </div>

          <div className="card-body">
            {error && (
              <div className="flex items-center gap-2 p-3 mb-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {!requiresTwoFactor ? (
                <>
                  <div>
                    <label htmlFor="email" className="label">
                      Email
                    </label>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="input"
                      placeholder="admin@example.com"
                      required
                      autoComplete="email"
                      autoFocus
                    />
                  </div>

                  <div>
                    <label htmlFor="password" className="label">
                      Password
                    </label>
                    <input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="input"
                      placeholder="Enter your password"
                      required
                      autoComplete="current-password"
                    />
                  </div>
                </>
              ) : (
                <div>
                  <label htmlFor="totp" className="label">
                    Authentication Code
                  </label>
                  <input
                    id="totp"
                    type="text"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="input text-center text-xl tracking-widest font-mono"
                    placeholder="000000"
                    required
                    autoComplete="one-time-code"
                    autoFocus
                    maxLength={6}
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Enter the 6-digit code from your authenticator app
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary w-full"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : requiresTwoFactor ? (
                  'Verify'
                ) : (
                  'Sign in'
                )}
              </button>
            </form>

            {requiresTwoFactor && (
              <button
                type="button"
                onClick={() => {
                  setRequiresTwoFactor(false);
                  setTotpCode('');
                  clearError();
                }}
                className="mt-4 text-sm text-gray-500 hover:text-gray-300 transition-colors w-full text-center"
              >
                Use a different account
              </button>
            )}
          </div>
        </div>

        <p className="text-center text-xs text-gray-600 mt-6">
          Setype Pulse - Internal Infrastructure Dashboard
        </p>
      </div>
    </div>
  );
}
